<template>
  <Dialog
    v-model="useDialogStore().addonEdit.isOpen"
    size="small-hug"
    @open="onOpen"
    @close="onClose"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="useT('addon.edit.title')"
        :show-close="true"
        @close="close"
      >
        <Badge
          v-if="form.status"
          :text="_upperFirst(form.status)"
          :type="statusTypeMap[form.status]"
        />
      </DialogHeader>
    </template>

    <div
      v-if="isLoading"
      v-loading="true"
      class="h-80 w-full"
    />
    <div v-else>
      <Form
        ref="formRef"
        :model="form"
        :rules="rules"
        @submit="onSubmit"
      >
        <InputBlock
          :label="useT('addon.create.type')"
          prop="type"
          direction="horizontal"
        >
          <Select
            :model-value="'ai'"
            :options="[{ id: 'ai', text: useT('addon.type.ai') }]"
            disabled
          />
        </InputBlock>

        <InputBlock
          :label="useT('addon.create.period')"
          prop="refillPeriod"
          direction="horizontal"
        >
          <RadioGroup
            :model-value="form.refillPeriod"
            :options="[
              { id: 'oneTime', text: useT('addon.period.oneTime') },
              { id: 'monthly', text: useT('addon.period.monthly') },
            ]"
            disabled
            @update:model-value="(value) => _set(form, 'refillPeriod', value)"
          />
        </InputBlock>

        <InputBlock
          :label="useT('addon.create.tier')"
          prop="tier"
          direction="horizontal"
        >
          <Select
            :model-value="form.tier"
            :options="tierOptions"
            @update:model-value="(value) => _set(form, 'tier', value)"
          />
        </InputBlock>

        <InputBlock
          v-if="form.tier === 'custom'"
          :label="useT('addon.create.customCredits')"
          prop="customCredits"
          direction="horizontal"
        >
          <NumberInput
            :model-value="form.customCredits"
            class="w-full"
            :min="1"
            @update:model-value="(value) => _set(form, 'customCredits', value)"
          />
        </InputBlock>

        <div class="mb-4 text-xs text-gray-500">
          {{ useT(form.refillPeriod === 'monthly' ? 'addon.edit.monthlyHint' : 'addon.edit.oneTimeHint') }}
        </div>

        <InputBlock
          v-if="form.refillPeriod === 'monthly'"
          :label="useT('addon.expiringAt')"
          prop="expiringAt"
          direction="horizontal"
        >
          <DatePicker
            v-model="form.expiringAt"
            :placeholder="useT('addon.expiringAt.none')"
            class="w-full"
          />
        </InputBlock>

        <Button
          v-if="form.status === 'active'"
          type="danger-text"
          icon-left="trash-01"
          class="mt-5"
          :loading="isDeactivating"
          @click="handleDeactivate"
        >
          {{ useT('addon.edit.deactivate') }}
        </Button>
      </Form>
    </div>

    <template
      v-if="!isLoading"
      #footer
    >
      <ButtonGroup
        type="primary"
        :text-submit="useT('bcd15')"
        :text-cancel="useT('bcd205')"
        align="left"
        :loading="isSubmitting"
        @submit="formRef.onSubmit()"
        @cancel="useDialogStore().close('addonEdit')"
      />
    </template>
  </Dialog>
</template>

<script setup>
const tiers = [
  { id: 'start', label: 'Start', credits: 50 },
  { id: 'plus', label: 'Plus', credits: 150 },
  { id: 'max', label: 'Max', credits: 500 },
];

const tierOptions = computed(() => [
  ...tiers.map((tier) => ({ id: tier.id, text: `${tier.label} - ${useT('addon.xCredits', { value: tier.credits })}` })),
  { id: 'custom', text: useT('addon.tier.custom') },
]);

const statusTypeMap = {
  active: 'success',
  expired: 'warning',
  used: 'neutral',
  deactivated: 'critical',
};

const form = ref({});
const rules = {};

const isLoading = ref(false);
const isSubmitting = ref(false);
const isDeactivating = ref(false);
const formRef = ref(null);

const tierFromCredits = (credits) => {
  const match = tiers.find((tier) => tier.credits === credits);
  return match ? match.id : 'custom';
};

const onOpen = async() => {
  isLoading.value = true;

  const addon = await useAddonsStore().fetchAddon({
    addonId: useDialogStore().addonEdit.id,
    workspaceDomain: useDialogStore().addonEdit.workspaceDomain,
  });

  if (!addon) {
    useDialogStore().close('addonEdit');
    return;
  }

  const credits = addon.data?.credits || 0;
  form.value = {
    _id: addon._id,
    status: addon.status,
    expiringAt: addon.expiringAt,
    refillPeriod: addon.data?.refillPeriod === 'monthly' ? 'monthly' : 'oneTime',
    tier: tierFromCredits(credits),
    customCredits: credits,
  };

  isLoading.value = false;
};

const onClose = () => {
  form.value = {};
};

const resolveCredits = () => {
  if (form.value.tier === 'custom') return Number(form.value.customCredits) || 0;
  return tiers.find((tier) => tier.id === form.value.tier)?.credits || 0;
};

const onSubmit = async() => {
  const credits = resolveCredits();
  if (!credits || credits < 1) return;

  isSubmitting.value = true;

  const response = await useAddonsStore().update({
    addonId: form.value._id,
    body: {
      workspace: useDialogStore().addonEdit.workspaceDomain,
      data: {
        refillPeriod: form.value.refillPeriod === 'monthly' ? 'monthly' : null,
        credits,
      },
      expiringAt: form.value.expiringAt || null,
    },
  });

  isSubmitting.value = false;

  if (response) {
    useToast(useT('bcd138'));
    const onSubmitCallback = useDialogStore().addonEdit.onSubmit;
    useDialogStore().close('addonEdit');
    if (typeof onSubmitCallback === 'function') onSubmitCallback(response);
  }
};

const handleDeactivate = () => {
  useModalStore().open({
    type: 'danger',
    title: useT('addon.edit.deactivateConfirm'),
    description: useT('addon.edit.deactivateConfirmDescription'),
    submitButtonText: useT('addon.edit.deactivateConfirmSubmit'),
    cancelButtonText: useT('bcd205'),
    onSubmit: async() => {
      isDeactivating.value = true;

      const response = await useAddonsStore().deactivate({
        addonId: form.value._id,
        workspaceDomain: useDialogStore().addonEdit.workspaceDomain,
      });

      isDeactivating.value = false;

      if (response) {
        useToast(useT('addon.edit.deactivateSuccess'));
        const onSubmitCallback = useDialogStore().addonEdit.onSubmit;
        useDialogStore().close('addonEdit');
        if (typeof onSubmitCallback === 'function') onSubmitCallback();
      }
    },
  });
};
</script>
